import React from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import TasksForm from "../../WorkOrder/Forms/TasksForm";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { useMutation } from "@tanstack/react-query";
import { useSectionChanges } from "../../../hooks/useSectionChanges";

const CUSTOM_TASK_TYPE = "COMPLETE_CUSTOM_TASK";

const hasSaveableTasks = (tasks) =>
  (tasks ?? []).every((task) => task?.taskType !== CUSTOM_TASK_TYPE || !!task?.title?.trim());

const EditTasksModal = ({ open, close, workOrderData }) => {
  const dispatch = useDispatch();
  const { updateWorkOrder } = useUpdateWorkOrder();

  const tasks = useSelector((state) => state.workOrder.payment.tasks);
  const { hasChanges, onHydrate } = useSectionChanges({ tasks });
  const canSave = hasSaveableTasks(tasks);
  const formatWorkOrderTasks = (tasksList) => {
    return tasksList.map((task) => {
      const {
        id,
        taskType,
        emailsToNotify,
        phone,
        reason,
        signatureFrom,
        title,
        description,
        ...rest
      } = task;
      const sanitizeEmails = (emailsToNotify || [])
        .filter((emailObj) => emailObj && (typeof emailObj === "string" || emailObj.email))
        .map((emailObj) => (typeof emailObj === "string" ? emailObj : emailObj.email));

      return {
        id,
        taskType,
        phone,
        reason,
        signatureFrom,
        title,
        description,
        emailsToNotify: sanitizeEmails,
      };
    });
  };

  const {
    isPending: isUpdatingLoading,
    error: isUpdatingError,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: (data) => {},
  });

  const handleSubmit = async () => {
    try {
      await updateWorkOrderApiMutation({
        id: workOrderData.id,
        tasks: formatWorkOrderTasks(tasks),
      });
      updateWorkOrder();

      dispatch(
        showToast({
          message: "Tasks updated successfully!",
          type: "success",
        }),
      );

      close(false);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          message: "Failed to update tasks.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-7 px-12 xl:w-[1400px] lg:w-[1000px] sm:w-[800px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Tasks</h6>

        <div className="mb-2 mt-3">
          <TasksForm
            isEditMode={true}
            workOrderData={{ tasks: workOrderData?.tasks }}
            onHydrate={onHydrate}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || !canSave || isUpdatingLoading}
        >
          {isUpdatingLoading ? "Saving..." : "Save Changes"}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditTasksModal;
